import { TENANT_CURRENCY } from "./currency";
import { DECIMAL_CONFIG, computedDecimalSchema, decimalPlainText, decimalRound } from "./decimal";

const MONEY = new Intl.NumberFormat("en-US", { style: "currency", currency: TENANT_CURRENCY });
const CURRENCY_SYMBOL = MONEY.formatToParts(0).find(isCurrencyPart)?.value ?? TENANT_CURRENCY;
const QUANTITY = new Intl.NumberFormat("en-US", { maximumFractionDigits: 8 });

type Numeric = number | string;

function asFiniteNumber(value: Numeric): number | null {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatMoney(value: Numeric): string {
  const parsed = computedDecimalSchema.safeParse(value);
  return parsed.success ? formatRawMoney(decimalRound(parsed.data, DECIMAL_CONFIG.moneyDecimalPlaces)) : String(value);
}

export function formatFixedAmount(value: Numeric): string {
  const parsed = computedDecimalSchema.safeParse(value);
  return parsed.success ? formatRawDecimal(decimalRound(parsed.data, DECIMAL_CONFIG.moneyDecimalPlaces)) : String(value);
}

export function formatQuantity(value: Numeric): string {
  if (typeof value === "string") return formatRawDecimal(value);
  const amount = asFiniteNumber(value);
  return amount === null ? String(value) : QUANTITY.format(amount);
}

function isCurrencyPart(part: Intl.NumberFormatPart): boolean {
  return part.type === "currency";
}

export function formatRawDecimal(value: Numeric): string {
  const parsed = computedDecimalSchema.safeParse(value);
  if (!parsed.success) return String(value);
  const [integer, fraction] = decimalPlainText(parsed.data).split(".");
  const sign = integer.startsWith("-") ? "-" : "";
  const whole = integer.replace(/^[+-]/, "") || "0";
  const digits = sign + whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction === undefined ? digits : `${digits}.${fraction}`;
}

export function formatRawMoney(value: Numeric): string {
  if (!computedDecimalSchema.safeParse(value).success) return String(value);
  const text = formatRawDecimal(value);
  return text.startsWith("-") ? `-${CURRENCY_SYMBOL}${text.slice(1)}` : `${CURRENCY_SYMBOL}${text}`;
}
